<template>
  <div class="rawTable">
    <div class="raw-table-header">
      <div class="parameter-container">
        <div class="parameter-row" v-for="[key, value] of Object.entries(keyValueHeader || {})">
          <div>{{$t(`statistics.param.${key}`)}}</div>
          <div>{{value && value.tr ? $t(value.tr) : value}}</div>
        </div>
      </div>
      <div class="buttons">
        <div class="action print" @click="print" :aria-label="$t('print')">
          <svgicon src="icon/printer.svg" :height="20" :width="20"></svgicon>
        </div>
        <div class="action download" @click="downloadAsCSV" :aria-label="$t('csv_download')">
          <svgicon src="icon/download.svg" :height="22" :width="22"></svgicon>
        </div>
      </div>
    </div>

    <div class="table-holder">
      <table class="raw-table-report" :class="{firstRowBold}">
        <thead>
          <tr>
            <th v-for="(column, columnIndex) of columnNamesWithBR" :class="tdClasses[columnIndex]" v-html="column"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="hasTotal && !isEmpty">
            <td v-for="(total, columnIndex) of columnTotals" class="total" :class="tdClasses[columnIndex]">{{total}}</td>
          </tr>
          <tr v-for="row of formattedRows">
            <td v-for="(cell, columnIndex) of row" :class="tdClasses[columnIndex]">{{cell}}</td>
          </tr>
          <tr v-if="hasTotal && !isEmpty">
            <td v-for="(total, columnIndex) of columnTotals" class="total" :class="tdClasses[columnIndex]">{{total}}</td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
  import { DateTime } from "luxon";

  export default {
    name: "RawTable",

    data() {
      return {
        MAX_COL_NAME: 16
      }
    },

    props: {
      firstRowBold: Boolean,
      keyValueHeader: {
        type: Object,
        required: false
      },
      columns: Array,
      rows: Array,
      csvFileName: {
        type: String,
        default: 'report'
      }
    },

    computed: {
      columnNames() {
        return this.columns.map(c => this.$to(c) || this.$t("statistics.column." + c.name));
      },
      columnNamesWithBR() {
        return this.columnNames.map(c => c && c.length > this.MAX_COL_NAME ? c.split(' ').join('<br>') : c)
      },
      formattedRows() {
        return this.rows.map(row => row.map((cell, index) => this.format(cell, index)))
      },
      columnTotals() {
        return this.columns.map((c, i) => c.computeTotal
          ? this.format(this.rows.reduce((acc, r) => acc + (parseFloat(r[i]) || 0), 0), i)
          : (i === 0 ? 'Total' : '---')
        )
      },
      isEmpty() {
        return !this.rows.length
      },
      hasTotal() {
        return this.columns.filter(c => !!c.computeTotal).length && this.rows.length > 1;
      },
      tdClasses() {
        return this.columns.map(c => {
          let format = c.format;
          if (format === 'money') {
            return ['money']
          } else {
            return []
          }
        })
      }
    },

    methods: {
      format(value, columnIndex) {
        let format = this.columns[columnIndex].format;

        /* Renaming alias of formats
         * */
        if (format === 'translation') {
          format = 'transObject'
        }
        /* Remapping common 'error'
         * */
        if (value === null || value === "null") {
          return '';
        }
        if (format === 'transObject' && typeof value === 'string') {
          value = this.tryParseJson(value)
        }

        if (!format) {
          return value
        } else if (format === 'transObject' && typeof value === 'object') {
          return this.$to(value)
        } else if (format === 'trans') {
          let prefix = this.columns[columnIndex].prefix || '';
          return this.$t(`${prefix}${prefix ? '.' : ''}${value}`)
        } else if (format === 'money') {
          return `${parseFloat(value).toFixed(2)}`
        } else if (format === 'datetime' && value.date) {
          let luxonDate = DateTime.fromFormat(value.date.substring(0, 19), 'yyyy-MM-dd HH:mm:ss');
          return luxonDate.invalid ? value.date : luxonDate.toLocaleString(DateTime.DATETIME_MED);
        } else if (format === 'month') {
          value = value.toString();
          return `${value.slice(0,4)}-${value.slice(4)}`;
        } else if (format === 'hour') {
          return `${value.toString().padStart(2, '0')}:00`;
        } else if (format === 'week_day') {
          value = value.toString();
          let tr = '';
          if      (value === '1') { tr = 'sunday' }
          else if (value === '2') { tr = 'monday' }
          else if (value === '3') { tr = 'tuesday' }
          else if (value === '4') { tr = 'wednesday' }
          else if (value === '5') { tr = 'thursday' }
          else if (value === '6') { tr = 'friday' }
          else if (value === '7') { tr = 'saturday' }
          return this.$t(tr);
        } else if (format === 'deliveryMethod') {
          let translationPath = `deliveryMethod.${value}`;
          let translated = this.$t(translationPath);
          return translated === translationPath ? value : translated;
        } else if (format === 'appName') {
          let translationPath = `appName.${value}`;
          let translated = this.$t(translationPath);
          return translated === translationPath ? value : translated;
        } else {
          return value
        }
      },
      tryParseJson(str) {
        try {
          return JSON.parse(str);
        } catch (e) {
          return str;
        }
      },
      print() {
        window.print();
      },
      downloadAsCSV() {
        window._downloadAsCsv(
          this.csvFileName,
          [].concat([this.columnNames]).concat(this.formattedRows)
        );
      }
    }
  }
</script>

<style lang="scss">
.raw-table-report {
  width: 100%;
  margin-top: 20px;

  td, th {
    padding: 10px 10px;
    text-align: left;

    &.money {
      text-align: right;
    }
    &.total {
      font-weight: bolder;
    }
  }
  th {
    white-space: nowrap;
    color: #808080;
    font-weight: 400;
    font-size: 13px;
    vertical-align: bottom;
  }
  td {
    font-size: 14px;
  }
  tbody tr:nth-child(odd) {
    td {
      background-color: #f5f5f5;
      color: #1a1a1a;
    }
    td:first-child {
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
    }
    td:last-child {
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
    }
  }
  tr {
    page-break-inside: avoid;
  }
}
</style>

<style lang="scss" scoped>
  .table-holder {
    overflow-x: auto;
    transform: rotateX(180deg);
  }
  .raw-table-report {
    transform: rotateX(180deg);
  }
  .firstRowBold {
    tr {
      td:first-child {
        font-weight: bold;
      }
      th:first-child {
        font-weight: 500;
      }
    }
  }
  .raw-table-header {
    display: flex;
    justify-content: space-between;

    .parameter-container {
      width: 300px;

      .parameter-row {
        display: flex;
        justify-content: space-between;
      }
    }

    .buttons {
      display: flex;
      align-items: center;
      fill: #fc6650;
      margin-right: -10px;

      .action {
        padding: 5px 10px;
        cursor: pointer;

        svg {
          cursor: pointer;
        }
      }

    }
  }
</style>